import { browserLocales } from './Utils';

export const PLAYER_PLACE_DETAILS_FIELDS =
  'businessStatus,reviews,rating,userRatingCount,currentOpeningHours,currentSecondaryOpeningHours';

export const CREATE_DEMO_PLACE_DETAILS_FIELDS =
  'formattedAddress,displayName,primaryType,addressComponents,nationalPhoneNumber,googleMapsUri,reservable,websiteUri,location,delivery';

const cache = new Map();
const inFlight = new Map();

const MAX_RETRIES = 4;
const BASE_DELAY_MS = 800;

export function getPlayerPlaceDetailsLanguage() {
  return browserLocales(true)[0] === 'fr' ? 'fr' : 'en';
}

function buildCacheKey(posId, language, fields) {
  return `${posId}:${language}:${fields}`;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function resolveOptions(languageOrOptions) {
  if (languageOrOptions != null && typeof languageOrOptions === 'object') {
    return {
      language: languageOrOptions.language || getPlayerPlaceDetailsLanguage(),
      fields: languageOrOptions.fields || PLAYER_PLACE_DETAILS_FIELDS,
    };
  }
  return {
    language: languageOrOptions || getPlayerPlaceDetailsLanguage(),
    fields: PLAYER_PLACE_DETAILS_FIELDS,
  };
}

export function getCachedPlaceDetails(posId, languageOrOptions) {
  if (posId == null) {
    return null;
  }
  const { language, fields } = resolveOptions(languageOrOptions);
  return cache.get(buildCacheKey(posId, language, fields)) ?? null;
}

async function fetchPlaceDetailsOnce(posId, language, fields) {
  const url =
    `https://places.googleapis.com/v1/places/${posId}` +
    `?fields=${fields}&languageCode=${language}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`;
  const response = await fetch(url);
  if (response.status === 429) {
    const err = new Error('429');
    err.status = 429;
    throw err;
  }
  if (!response.ok) {
    const err = new Error(String(response.status));
    err.status = response.status;
    throw err;
  }
  return response.json();
}

/**
 * Place Details avec cache RAM + dédup in-flight + retry sur 429.
 * @param {string} posId
 * @param {string|{language?: string, fields?: string}} [languageOrOptions]
 */
export async function fetchPlaceDetails(posId, languageOrOptions) {
  if (posId == null) {
    return null;
  }

  const { language, fields } = resolveOptions(languageOrOptions);
  const key = buildCacheKey(posId, language, fields);
  const cached = cache.get(key);
  if (cached) {
    return cached;
  }

  const pending = inFlight.get(key);
  if (pending) {
    return pending;
  }

  const promise = (async () => {
    let lastError = null;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt += 1) {
      try {
        const data = await fetchPlaceDetailsOnce(posId, language, fields);
        if (data != null) {
          cache.set(key, data);
        }
        return data;
      } catch (error) {
        lastError = error;
        if (error?.status !== 429 || attempt === MAX_RETRIES - 1) {
          throw error;
        }
        const delay = BASE_DELAY_MS * Math.pow(2, attempt) + Math.floor(Math.random() * 250);
        await sleep(delay);
      }
    }
    throw lastError || new Error('place_details');
  })().finally(() => {
    inFlight.delete(key);
  });

  inFlight.set(key, promise);
  return promise;
}
